import React, { useState } from 'react';
import { ArrowLeft, Building2, MapPin, Plus, Edit2, Check, Clock, Users, Package, ChevronRight, Search, X, Save } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { REGIONS } from '../data/database';

const CompanyOfficesPage = ({ onBack, onNavigate }) => {
  const { currentCompany, companyOffices, selectedOffice, switchOffice } = useApp();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newOffice, setNewOffice] = useState({
    name: '',
    address: '',
    city: '',
    region: '',
    phone: '',
    email: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  const filteredOffices = companyOffices?.filter(office => 
    office.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    office.address?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const getOfficeStats = (office) => {
    return {
      services: office.services?.length || Math.floor(Math.random() * 10) + 5,
      staff: office.staff?.length || Math.floor(Math.random() * 5) + 2,
      bookings: Math.floor(Math.random() * 50) + 10
    };
  };

  const handleSaveNewOffice = async () => {
    if (!newOffice.name || !newOffice.address) return;
    setIsSaving(true);
    // Simulate save
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSaving(false);
    setShowAddModal(false);
    setNewOffice({ name: '', address: '', city: '', region: '', phone: '', email: '' });
    // In real app, would add to database and refresh
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-br from-amber-500 to-orange-500 pt-6 pb-8">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
            data-testid="company-offices-back"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Indietro</span>
          </button>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Building2 className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-white">Gestione Sedi</h1>
                <p className="text-amber-100 text-sm truncate">{currentCompany?.name || 'La tua azienda'}</p>
              </div>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-1.5 bg-white text-amber-600 px-3 py-2 rounded-xl font-medium hover:bg-amber-50 transition-colors text-sm flex-shrink-0"
              data-testid="add-office-btn"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Nuova Sede</span>
              <span className="sm:hidden">Aggiungi</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 -mt-4">
        {/* Search */}
        <div className="bg-white rounded-2xl shadow-sm p-3 sm:p-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cerca sede..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
              data-testid="office-search-input"
            />
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
          <div className="bg-white rounded-xl p-3 text-center shadow-sm">
            <p className="text-xl sm:text-2xl font-bold text-amber-600">{companyOffices?.length || 0}</p>
            <p className="text-xs text-gray-500">Sedi</p>
          </div>
          <div className="bg-white rounded-xl p-3 text-center shadow-sm">
            <p className="text-xl sm:text-2xl font-bold text-emerald-600">
              {companyOffices?.reduce((acc, o) => acc + (o.staff?.length || 3), 0) || 0}
            </p>
            <p className="text-xs text-gray-500">Operatori</p>
          </div>
          <div className="bg-white rounded-xl p-3 text-center shadow-sm">
            <p className="text-xl sm:text-2xl font-bold text-blue-600">
              {companyOffices?.reduce((acc, o) => acc + (o.services?.length || 8), 0) || 0}
            </p>
            <p className="text-xs text-gray-500">Servizi</p>
          </div>
        </div>

        {/* Offices List */}
        <div className="space-y-3">
          {filteredOffices.map((office) => {
            const stats = getOfficeStats(office);
            const isActive = office.id === selectedOffice?.id;
            
            return (
              <div 
                key={office.id}
                className={`bg-white rounded-2xl shadow-sm overflow-hidden transition-all ${isActive ? 'ring-2 ring-emerald-500' : ''}`}
                data-testid={`office-card-${office.id}`}
              >
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Office Icon */}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                      <MapPin className={`w-5 h-5 ${isActive ? 'text-emerald-600' : 'text-gray-400'}`} />
                    </div>
                    
                    {/* Office Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="min-w-0">
                          <h3 className="font-semibold text-gray-900 text-sm sm:text-base leading-tight">{office.name}</h3>
                          {isActive && (
                            <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs px-1.5 py-0.5 rounded-full mt-1">
                              <Check className="w-3 h-3" />
                              Attiva
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => switchOffice(office.id)}
                          className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors flex-shrink-0 ${
                            isActive 
                              ? 'bg-gray-100 text-gray-400 cursor-default' 
                              : 'bg-emerald-500 text-white hover:bg-emerald-600'
                          }`}
                          disabled={isActive}
                          data-testid={`switch-to-office-${office.id}`}
                        >
                          {isActive ? 'Attiva' : 'Seleziona'}
                        </button>
                      </div>
                      
                      <p className="text-xs text-gray-500 mb-2 line-clamp-1">
                        {office.address}
                      </p>
                      
                      {/* Quick Stats - compact on mobile */}
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <Package className="w-3 h-3" />
                          {stats.services}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {stats.staff}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {stats.bookings}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Quick Actions Footer */}
                <div className="border-t border-gray-100 px-4 py-2.5 bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => { switchOffice(office.id); onNavigate('office-profile'); }}
                      className="flex items-center gap-1 text-xs text-gray-600 hover:text-emerald-600 transition-colors"
                    >
                      <Edit2 className="w-3 h-3" />
                      Modifica
                    </button>
                    <button
                      onClick={() => { switchOffice(office.id); onNavigate('office-catalog'); }}
                      className="flex items-center gap-1 text-xs text-gray-600 hover:text-blue-600 transition-colors"
                    >
                      <Package className="w-3 h-3" />
                      Catalogo
                    </button>
                    <button
                      onClick={() => { switchOffice(office.id); onNavigate('office-operators'); }}
                      className="flex items-center gap-1 text-xs text-gray-600 hover:text-purple-600 transition-colors"
                    >
                      <Users className="w-3 h-3" />
                      Team
                    </button>
                  </div>
                  <button
                    onClick={() => { switchOffice(office.id); onNavigate('office-profile'); }}
                    className="flex items-center gap-0.5 text-xs text-emerald-600 font-medium hover:text-emerald-700 transition-colors"
                  >
                    Gestisci
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredOffices.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
            <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <h3 className="font-medium text-gray-900 text-sm mb-1">Nessuna sede trovata</h3>
            <p className="text-xs text-gray-500 mb-4">
              {searchQuery ? 'Prova a modificare la ricerca' : 'Aggiungi la tua prima sede'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center gap-2 bg-amber-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-amber-600 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Aggiungi Sede
              </button>
            )}
          </div>
        )}

        {/* Help Card */}
        <div className="mt-4 bg-amber-50 rounded-xl p-4 border border-amber-100">
          <h3 className="font-medium text-amber-800 text-sm mb-1">Come funziona</h3>
          <p className="text-xs text-amber-700">
            Ogni sede ha configurazione separata. Seleziona una sede per modificarne servizi, team e orari.
          </p>
        </div>
      </div>

      {/* Add Office Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Nuova Sede</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome Sede *</label>
                <input
                  type="text"
                  value={newOffice.name}
                  onChange={(e) => setNewOffice({...newOffice, name: e.target.value})}
                  placeholder="Es: Milano Centro"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  data-testid="new-office-name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Indirizzo *</label>
                <input
                  type="text"
                  value={newOffice.address}
                  onChange={(e) => setNewOffice({...newOffice, address: e.target.value})}
                  placeholder="Via Roma, 123"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  data-testid="new-office-address"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Città</label>
                  <input
                    type="text"
                    value={newOffice.city}
                    onChange={(e) => setNewOffice({...newOffice, city: e.target.value})}
                    placeholder="Milano"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Regione</label>
                  <select
                    value={newOffice.region}
                    onChange={(e) => setNewOffice({...newOffice, region: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
                  >
                    <option value="">Seleziona...</option>
                    {REGIONS?.map(region => (
                      <option key={region.slug} value={region.slug}>{region.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                  <input
                    type="tel"
                    value={newOffice.phone}
                    onChange={(e) => setNewOffice({...newOffice, phone: e.target.value})}
                    placeholder="+39 02 1234567"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={newOffice.email}
                    onChange={(e) => setNewOffice({...newOffice, email: e.target.value})}
                    placeholder="sede@email.it"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs text-gray-500">
                  Dopo aver creato la sede, potrai configurare servizi, prodotti, orari e team dalla pagina di gestione.
                </p>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 flex gap-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleSaveNewOffice}
                disabled={!newOffice.name || !newOffice.address || isSaving}
                className="flex-1 py-3 bg-amber-500 text-white rounded-xl text-sm font-medium hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="save-new-office"
              >
                <Save className="w-4 h-4" />
                {isSaving ? 'Salvataggio...' : 'Crea Sede'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyOfficesPage;
